import { useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { Dialog, Field, Text } from '@loadsmart/miranda-react'

import { useSession } from 'auth/ui/hooks/useSession'
import type { RequestType } from 'core/domain/RequestType'
import { requestZendesk } from 'core/use-cases/tryRequestZendesk'

import { StyledDialogDescription, StyledTextField } from './TryNowZendeskDialog.styles'

interface ZendeskDialogProps {
  requestType: RequestType
  open: boolean
  onClose: () => void
  // Callers that ask for something specific — early access to a connector, a
  // report — can say so here instead of leaving the visitor to guess what they
  // just clicked.
  title?: string
  description?: string
}

const DEFAULT_TITLE = "We'll be in touch"

const ZendeskDialog = ({ open, requestType, onClose, title, description }: ZendeskDialogProps) => {
  const session = useSession()

  const requestZendeskMutation = useMutation({
    mutationFn: () => requestZendesk(requestType, name, company, email, phone),
    onSuccess: () => {
      toast("Your request has been sent. Soon we'll get in touch.", { type: 'success' })

      onClose()
      resetForm()
    },
    onError: () => {
      toast('Your request was not sent. Please contact Loadsmart team to handle the request.', {
        type: 'error',
      })
    },
  })

  const [name, setName] = useState<string>(
    session?.user.first_name && session?.user.last_name
      ? session?.user.first_name + ' ' + session?.user.last_name
      : ''
  )
  const [company, setCompany] = useState<string>(session?.user.client || '')
  const [email, setEmail] = useState<string>(session?.user.email || '')
  const [phone, setPhone] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string>('')

  function resetForm() {
    setName(
      session?.user.first_name && session?.user.last_name
        ? session?.user.first_name + ' ' + session?.user.last_name
        : ''
    )
    setCompany(session?.user.client || '')
    setEmail(session?.user.email || '')
    setPhone('')
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^(\+?\d{1,2}\s?)?(\(\d{3}\)|\d{3})[-.\s]?\d{3}[-.\s]?\d{4}$/
    return phoneRegex.test(phone)
  }

  const handleSendRequest = async () => {
    const isEmailValid = validateEmail(email)
    const isPhoneValid = validatePhone(phone)

    if (!isEmailValid) {
      setEmailError('Invalid email address')
    } else {
      setEmailError('')
    }

    if (!isPhoneValid) {
      setPhoneError('Invalid phone number')
    } else {
      setPhoneError('')
    }

    if (name && company && isEmailValid && isPhoneValid) {
      requestZendeskMutation.mutate()
    } else {
      alert('Please fill in all fields with valid information before submitting.')
    }
  }

  const handleClose = () => {
    onClose()
    resetForm()
  }

  const isDisabled =
    name.length <= 0 || company.length <= 0 || email.length <= 0 || phone.length <= 0

  return (
    <Dialog open={open} size="small" variant="neutral" onClose={handleClose}>
      <Dialog.Header>
        {title ?? DEFAULT_TITLE}
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        {description && (
          <StyledDialogDescription>
            <Text variant="body-md" color="color-text-secondary">
              {description}
            </Text>
          </StyledDialogDescription>
        )}
        <Field required>
          <Field.Label id="nameLabel">Name</Field.Label>
          <StyledTextField
            aria-labelledby="nameLabel"
            aria-required="true"
            value={name}
            onInput={event => {
              if (event.target) {
                const inputElement = event.target as unknown as HTMLInputElement
                setName(inputElement.value)
              }
            }}
          />
        </Field>
        <Field required>
          <Field.Label id="companyLabel">Company</Field.Label>
          <StyledTextField
            aria-labelledby="companyLabel"
            aria-required="true"
            value={company}
            onInput={event => {
              if (event.target) {
                const inputElement = event.target as unknown as HTMLInputElement
                setCompany(inputElement.value)
              }
            }}
          />
        </Field>

        <Field required>
          <Field.Label id="emailLabel">Email</Field.Label>
          <StyledTextField
            aria-required="true"
            aria-labelledby="emailLabel"
            value={email}
            onInput={event => {
              if (event.target) {
                const inputElement = event.target as unknown as HTMLInputElement
                const value = inputElement.value
                setEmail(value)
                setEmailError(validateEmail(value) ? '' : 'Invalid email address')
              }
            }}
          />
          {emailError && <span style={{ color: 'red' }}>{emailError}</span>}
        </Field>

        <Field required>
          <Field.Label id="phoneLabel">Phone</Field.Label>
          <StyledTextField
            aria-labelledby="phoneLabel"
            aria-required="true"
            value={phone}
            onInput={event => {
              if (event.target) {
                const inputElement = event.target as unknown as HTMLInputElement
                const value = inputElement.value
                setPhone(value)
                setPhoneError(validatePhone(value) ? '' : 'Invalid phone number')
              }
            }}
          />
          {phoneError && <span style={{ color: 'red' }}>{phoneError}</span>}
        </Field>
      </Dialog.Body>
      <Dialog.Footer></Dialog.Footer>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={handleClose} disabled={isDisabled}>
          Cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          onClick={handleSendRequest}
          disabled={isDisabled}
          loading={requestZendeskMutation.isPending}
        >
          Send request
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}

export default ZendeskDialog
